// frontend/src/components/UI/ConfirmModal.js
/**
 * Reusable confirmation modal. Follows design-system-reference.md § Modal System.
 * Portal, modal-overlay, modal-container-sm, modal-header, modal-content, modal-footer.
 *
 * Props:
 *   isOpen        - boolean, controls visibility
 *   title         - string, modal heading
 *   message       - string, simple text body (ignored when children provided)
 *   children      - JSX, rich body content (takes precedence over message)
 *   confirmLabel  - string, confirm button text
 *   cancelLabel   - string, cancel button text (default "Cancel")
 *   variant       - 'danger' | 'primary' (default 'danger')
 *   onConfirm     - callback on confirm
 *   onCancel      - callback on cancel / dismiss
 *   isLoading     - boolean, shows spinner and disables buttons
 *   confirmDisabled - boolean, disables the confirm button (e.g. required input not yet provided)
 *   theme         - 'admin' | 'employee' (default 'admin')
 */

import React, { useEffect } from 'react';
import Modal from './Modal';

const ConfirmModal = ({
  isOpen,
  title,
  message,
  children,
  confirmLabel,
  cancelLabel = 'Cancel',
  variant = 'danger',
  onConfirm,
  onCancel,
  isLoading = false,
  confirmDisabled = false,
  theme = 'admin',
}) => {
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production' && isOpen && typeof onCancel !== 'function') {
      console.error(
        '[ConfirmModal] Missing required `onCancel` callback. ' +
        'Cancel button and close icon cannot dismiss the modal without it.'
      );
    }
    if (process.env.NODE_ENV !== 'production' && isOpen && typeof onConfirm !== 'function') {
      console.error(
        '[ConfirmModal] Missing required `onConfirm` callback. ' +
        'Confirm action will not run without it.'
      );
    }
  }, [isOpen, onCancel, onConfirm]);

  const handleCancel = typeof onCancel === 'function' ? onCancel : () => {};
  const handleConfirm = typeof onConfirm === 'function' ? onConfirm : () => {};

  const focusRing = theme === 'admin' ? 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500' : 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500';
  const cancelClass = theme === 'admin' ? 'btn btn-admin-outline' : 'btn btn-outline';
  const confirmClass = variant === 'danger' ? 'btn btn-danger' : (theme === 'admin' ? 'btn btn-admin-primary' : 'btn btn-primary');

  const footer = (
    <>
      <button
        type="button"
        onClick={handleCancel}
        className={`${cancelClass} ${focusRing}`}
        disabled={isLoading}
      >
        {cancelLabel}
      </button>
      <button
        type="button"
        onClick={handleConfirm}
        className={`${confirmClass} ${focusRing}`}
        disabled={isLoading || confirmDisabled}
      >
        {isLoading ? (
          <>
            <span className="loading-spinner-sm mr-2" />
            Processing...
          </>
        ) : (
          confirmLabel
        )}
      </button>
    </>
  );

  return (
    <Modal isOpen={isOpen} onClose={handleCancel} title={title} size="sm" footer={footer} theme={theme}>
      {children || <p className="text-sm text-gray-700">{message}</p>}
    </Modal>
  );
};

export default ConfirmModal;
